import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

type TaskControlSize = 'sm' | 'lg';

interface TaskControlBaseProps {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  disabled?: boolean;
}

/** `sm` (default): compact w-[100px] checkbox-only button (tree editor). */
interface TaskControlSmProps extends TaskControlBaseProps {
  size?: 'sm';
  title?: never;
}

/** `lg`: full-width labeled task button (question detail panel). */
interface TaskControlLgProps extends TaskControlBaseProps {
  size: 'lg';
  title: string;
}

type TaskControlProps = TaskControlSmProps | TaskControlLgProps;

/**
 * Checkbox control for task fields.
 *
 * Visual variants:
 *  - `sm` (default): w-[100px], h-8, checkbox-only button (tree editor).
 *  - `lg`: w-full, h-12, border-2, rounded-lg, includes a label rendered
 *    next to the checkbox (line-through when checked). `title` is required
 *    for `lg` and not allowed for `sm`.
 */
export function TaskControl(props: TaskControlProps) {
  const { value, onChange, disabled } = props;
  const isLg = props.size === 'lg';

  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'flex items-center transition-colors',
        isLg
          ? `h-12 w-full gap-3 rounded-lg border-2 px-4 text-left transition-all
            focus-visible:ring-2`
          : 'h-8 w-[100px] shrink-0 justify-center rounded-md border focus-visible:ring-1',
        'focus-visible:ring-ring focus-visible:outline-none',
        value === true
          ? isLg
            ? 'border-primary bg-primary/5'
            : 'border-primary bg-primary/5'
          : isLg
            ? 'border-border hover:border-primary/30 hover:bg-muted/30'
            : 'hover:bg-muted/50 border-input',
        disabled && 'cursor-not-allowed opacity-50',
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        onChange(value ? null : true);
      }}
    >
      <Checkbox
        checked={value === true}
        disabled={disabled}
        aria-label="Task complete"
        className="pointer-events-none"
      />
      {isLg && (
        <span
          className={cn('text-sm', value === true && 'line-through opacity-60')}
        >
          {props.title}
        </span>
      )}
    </button>
  );
}
